import { useFeatureFlag } from '@src/hooks';
import { useCustomerUsersByID } from '@src/hooks/useCustomer';
import { useAppStateDispatch, useAppStateSelector } from '@src/providers/AppStateProvider';
import { getRestFailureUserMessage } from '@src/utils/rtkQuery/restFailureError';
import {
  NotificationVariants,
  useDynamicCallback,
  useGlobalToasts,
  type CustomerUser,
  type DrawerProps,
} from '@talos/kyoko';
import { useEffect, useMemo, useRef } from 'react';
import { useCustomerApiKeys } from './CustomerApiKeys/useCustomerApiKeys';
import { useCustomerUserDetails } from './CustomerUserDetails/useCustomerUserDetails';
import type { Warning } from './CustomerUserPermissions/types';
import { useCustomerUserPermissions } from './CustomerUserPermissions/useCustomerUserPermissions';
import { isLoading, isSaving } from './state/drawer/drawerSlice';
import { markPermissionsSaved } from './state/permissions/permissionsSlice';
import { selectIsDrawerDirty, selectTabDefs } from './state/slice';
import { markUserDetailsSaved } from './state/userDetails/userDetailsSlice';

export type CustomerUserDetailsDrawerProps = {
  customerUserID?: string;
  onClose: () => void;
};

export interface NewUserDrawerProps extends DrawerProps, CustomerUserDetailsDrawerProps {}

export function useCustomerUserDetailsDrawer(props: NewUserDrawerProps & { onWarning: (warning: Warning) => void }) {
  const state = useAppStateSelector(state => state.customerUserDrawer);
  const customerUsersByID = useCustomerUsersByID();
  const { add: addToast } = useGlobalToasts();
  const dispatch = useAppStateDispatch();

  const customerDetails = useCustomerUserDetails();
  const apiKeys = useCustomerApiKeys();
  const permissions = useCustomerUserPermissions();

  const customerUser = useMemo(
    () => (props.customerUserID ? customerUsersByID?.get(props.customerUserID) : undefined),
    [props.customerUserID, customerUsersByID]
  );

  const userSettingsRef = useRef<HTMLInputElement>(null);

  const loadCustomerAndFocus = useDynamicCallback(async (customerUser?: CustomerUser) => {
    dispatch(isLoading(true));
    await customerDetails.loadCustomerUser(customerUser);
    dispatch(isLoading(false));
    if (!customerUser) {
      // doesn't always focus without the timeout
      setTimeout(() => userSettingsRef.current?.focus());
    }
  });

  useEffect(() => {
    loadCustomerAndFocus(customerUser);
  }, [customerUser, loadCustomerAndFocus]);

  const closeDrawer = useDynamicCallback(() => {
    props.close();
    props.onClose();
  });
  const { enableCustomerAccountRestrictions } = useFeatureFlag();
  const isDrawerDirty = useAppStateSelector(state => selectIsDrawerDirty(state, enableCustomerAccountRestrictions));

  const handleClose = useDynamicCallback(() => {
    if (!props.isOpen) {
      // this component stays mounted even when closed so we don't want the esc key to continue to show this confirmation window
      return;
    }

    if (isDrawerDirty) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        closeDrawer();
      }
    } else {
      closeDrawer();
    }
  });

  const onSave = useDynamicCallback(async () => {
    try {
      dispatch(isSaving(true));
      await Promise.all([
        customerDetails.saveCustomerUser().then(customerUserId => permissions.savePermissions(customerUserId)),
        apiKeys.saveApiKeys(),
      ]);
      addToast({
        text: 'Customer user saved.',
        variant: NotificationVariants.Positive,
      });

      // Leave the drawer open after save so the user can keep editing. For new users, clear the
      // form so another user can be created quickly; for existing users, clear dirty state on every tab.
      if (state.userDetails.customerUserId) {
        dispatch(markUserDetailsSaved());
        dispatch(markPermissionsSaved());
      } else {
        loadCustomerAndFocus();
      }
    } catch (e) {
      // if something went wrong we don't want to close the drawer so any issues can be resolved and retried
      addToast({
        text: `Could not save customer user: ${
          (typeof e === 'string' ? e : getRestFailureUserMessage(e)) ?? 'Unknown error'
        }`,
        variant: NotificationVariants.Negative,
      });
    } finally {
      dispatch(isSaving(false));
    }
  });

  const handleSave = useDynamicCallback(async () => {
    // for each write permission, there must be a read permission for the same account, unless there are no read permissions in which case there is an implicit read permission for the account
    const readPermissions = state.permissions.customerAccounts.filter(p => p.permissions.includes('read'));
    if (
      state.permissions.customerAccounts.length === 0 ||
      readPermissions.length === 0 ||
      !state.permissions.customerAccounts.find(p => p.permissions.includes('write') && !p.permissions.includes('read'))
    ) {
      onSave();
    } else {
      props.onWarning({
        message: `Trade permission does not include View permissions for an account. ${
          customerUser?.DisplayName ?? 'New user'
        } will not be able to see orders or trades for this account. Do you want to proceed?`,
        onOk: onSave,
      });
    }
  });

  const tabDefs = useAppStateSelector(state => selectTabDefs(state, enableCustomerAccountRestrictions));

  const isSaveDisabled = !isDrawerDirty || state.drawer.isSaving;

  return {
    handleClose,
    handleSave,
    userSettingsRef,
    customerUser,
    tabDefs,
    isSaveDisabled,
    isLoading: state.drawer.isLoading,
  };
}
